import { useId, useRef } from "react";
import { fmtSize } from "@/lib/format";

type Props = {
  value: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  invalid?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
  inputLabel?: string;
};

export function FileUploader({
  value,
  onChange,
  accept,
  invalid,
  emptyTitle = "Upload your CV (PDF, DOC, DOCX)",
  emptyHint = "Up to 10 MB",
  inputLabel,
}: Props) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <>
      <label
        htmlFor={id}
        className={`block cursor-pointer rounded-xl border border-dashed bg-surface-alt p-6 text-center transition-colors ${
          invalid
            ? "border-danger bg-danger-soft"
            : "border-border-strong hover:border-primary hover:bg-primary-soft"
        } ${value ? "flex items-center gap-3.5 text-left" : ""}`}
      >
        {value ? (
          <>
            <span className="grid size-10 shrink-0 place-items-center rounded-md bg-success-soft text-[#166534]">
              <FileIcon />
            </span>
            <span className="flex-1">
              <span className="block text-sm font-semibold">{value.name}</span>
              <span className="block text-xs text-text-muted">
                {fmtSize(value.size)} · Selected
              </span>
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onChange(null);
                if (inputRef.current) inputRef.current.value = "";
              }}
              className="rounded-md p-1.5 text-text-muted hover:bg-surface-alt hover:text-danger"
              aria-label="Remove file"
            >
              <TrashIcon />
            </button>
          </>
        ) : (
          <>
            <span className="mx-auto mb-2.5 grid size-10 place-items-center rounded-md bg-surface text-primary">
              <FileIcon />
            </span>
            <span className="block text-sm font-semibold">{emptyTitle}</span>
            <span className="mt-1 block text-xs text-text-muted">{emptyHint}</span>
          </>
        )}
      </label>
      <input
        ref={inputRef}
        id={id}
        type="file"
        aria-label={inputLabel}
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          onChange(f);
        }}
      />
    </>
  );
}

function FileIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}
